import React, { useEffect, useState, useCallback } from 'react';
import { Form, Button, Card, Alert } from 'react-bootstrap';
import { Settings, Database, Globe, Code, Trash2 } from 'lucide-react';
import { API_ENDPOINTS } from "../../config/api.config";
import type {EmbeddingOption, KBUsageInfo} from '../../types/knowledge-base';
import type {
    ApiConfiguration,
    ExtendedFileMetadata
} from '../../types';
import FileSelectionModal from '../modals/knowledge-base/FileSelectionModal';
import WebsiteSelectionModal from '../modals/knowledge-base/WebsiteSelectionModal';
import ApiSelectionModal from '../modals/knowledge-base/ApiSelectionModal';
import { FileIcon } from '../data-sources/components/FileIcon';
import {apiService} from "../../services/apiService.ts";
import DeleteConfirmationModal from '../../components/modals/common/DeleteConfirmationModal';
import DeletedDataSourceItem from '../data-sources/components/DeletedDataSourceItem';

type SourceType = 'file_upload' | 'drive_sync' | 'web_source' | 'api_source' | 'api_connection' | 'api_query';
type DataSourceStatus = 'active' | 'pending' | 'failed' | 'error' | 'inactive' | 'deleted';
type ApiMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface ProcessedSource extends ExtendedDataSource {
    id: string;
    name: string | null;
    type: 'file' | 'web_source' | 'api_source';
    source_type: SourceType;
    created_at: string;
    updated_at?: string;
    status: DataSourceStatus;
    isDeleted?: boolean;
    metadata?: {
        original_metadata: {
            mime_type?: string;
            url?: string;
            crawl_depth?: number;
            method?: ApiMethod;
        };
    };
    configuration?: ApiConfiguration;
    description?: string;  // Required by ExtendedDataSource
}

interface ExtendedDataSource {
    id: string;
    name: string | null;
    type?: 'file' | 'web_source' | 'api_source';
    source_type: SourceType;
    created_at: string;
    updated_at?: string;
    metadata?: ExtendedFileMetadata;
    configuration?: ApiConfiguration;
    status: DataSourceStatus;
    isDeleted?: boolean;
}

const sourceTypes = [
    { value: 'files' as const, label: 'Files', icon: <FileIcon mimeType="text/plain" size={16} className="text-secondary" /> },
    { value: 'websites' as const, label: 'Websites', icon: <Globe size={16} /> },
    { value: 'apis' as const, label: 'APIs', icon: <Code size={16} /> }
];

const embeddingOptions: EmbeddingOption[] = [
    { value: 'text-embedding-ada-002', label: 'OpenAI Ada 002' },
    { value: 'bge-large-en', label: 'BGE Large English' },
    { value: 'gte-large', label: 'GTE Large' },
    { value: 'e5-large-v2', label: 'E5 Large v2' }
];

interface WebsiteMetadata {
    url?: string;
    crawl_depth?: number;
}

interface FormField {
    name: string;
    description?: string;
    sourceType: string;
    chunkSize: number;
    overlap: number;
    embeddingModel: string;
    dataSourceIds: string[];
}

type FormFieldValue = string | number | string[];

interface KnowledgeBaseFormProps {
    formData: FormField;
    mode: 'create' | 'edit';
    onChange: (field: keyof FormField, value: FormFieldValue) => void;
    onSubmit: (e: React.FormEvent) => void;
    onDelete?: () => void;
    error?: string | null;
    isLoading?: boolean;
    kbUsage?: KBUsageInfo | null;
    isClone?: boolean;
    status?: string;
}

// Add after other type/interface definitions
interface SourceResponse {
    id: string;
    name?: string;
    source_type?: SourceType;
    created_at: string;
    updated_at?: string;
    status?: DataSourceStatus;
    metadata?: {
        mime_type?: string;
        original_metadata?: Record<string, any>;
    };
    configuration?: {
        url?: string;
        endpoint?: string;
        method?: string;
        crawl_depth?: number;
    };
}

interface ListDataSourcesResponse {
    dataSources?: SourceResponse[];
    webSources?: SourceResponse[];
    apiSources?: SourceResponse[];
    pagination?: {
        total: number;
        page: number;
        totalPages: number;
        limit: number;
    };
}

const renderSectionHeader = (icon: React.ReactNode, title: React.JSX.Element, subtitle?: string) => (
    <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center">
            <div className="rounded-circle p-2 bg-secondary bg-opacity-10 me-2">
                {icon}
            </div>
            <h6 className="mb-0 fw-medium">{title}</h6>
        </div>
        {subtitle && <small className="text-muted">{subtitle}</small>}
    </div>
);



const KnowledgeBaseForm: React.FC<KnowledgeBaseFormProps> = ({
                                                                 formData,
                                                                 mode,
                                                                 onChange,
                                                                 onSubmit,
                                                                 onDelete,
                                                                 error,
                                                                 isLoading,
                                                                 kbUsage,
                                                                 status
                                                             }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [selectedDataSources, setSelectedDataSources] = useState<ProcessedSource[]>([]);
    const [isLoadingDataSources, setIsLoadingDataSources] = useState(true);
    const [dataSourceLoadingStates, setDataSourceLoadingStates] = useState<Record<string, boolean>>({});
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [showFileModal, setShowFileModal] = useState(false);
    const [showWebsiteModal, setShowWebsiteModal] = useState(false);
    const [showApiModal, setShowApiModal] = useState(false);

    const isFieldDisabled = (fieldName: string) => {
        if (mode !== 'edit') return false;
        if (status !== 'ready') return false;
        return !['name', 'description'].includes(fieldName);
    };





    const fetchSelectedDataSources = useCallback(async () => {
        if (!formData.dataSourceIds?.length) {
            setSelectedDataSources([]);
            setIsLoadingDataSources(false);
            return;
        }

        try {
            setFetchError(null);
            setIsLoadingDataSources(true);

            setDataSourceLoadingStates(prev => ({
                ...prev,
                ...formData.dataSourceIds.reduce((acc, id) => ({ ...acc, [id]: true }), {})
            }));

            const queryParams = new URLSearchParams({
                ids: formData.dataSourceIds.join(',')
            });

            let endpoint;
            switch (formData.sourceType) {
                case 'files':
                    endpoint = `${API_ENDPOINTS.DATA_SOURCE.LIST}?${queryParams}`;
                    break;
                case 'websites':
                    endpoint = `${API_ENDPOINTS.DATA_SOURCE.WEB_SOURCES.LIST}?${queryParams}`;
                    break;
                case 'apis':
                    endpoint = `${API_ENDPOINTS.DATA_SOURCE.API_SOURCES.LIST}?${queryParams}`;
                    break;
                default:
                    throw new Error('Invalid source type');
            }

            const data = await apiService.get<ListDataSourcesResponse>(endpoint);

            // Create a map of returned sources with proper typing
            const returnedSources = new Map<string, SourceResponse>();
            let sources: SourceResponse[] = [];
            switch (formData.sourceType) {
                case 'files':
                    sources = data.dataSources || [];
                    break;
                case 'websites':
                    sources = data.webSources || [];
                    break;
                case 'apis':
                    sources = data.apiSources || [];
                    break;
            }
            sources.forEach((source: SourceResponse) => returnedSources.set(source.id, source));

            const processedSources = formData.dataSourceIds.map<ProcessedSource>(id => {
                const source = returnedSources.get(id);
                if (!source) {
                    const type = formData.sourceType === 'files' ? 'file' :
                        formData.sourceType === 'websites' ? 'web_source' :
                            'api_source';
                    return {
                        id,
                        name: null,
                        type,
                        source_type: formData.sourceType === 'files' ? 'file_upload' :
                            formData.sourceType === 'websites' ? 'web_source' :
                                'api_source',
                        created_at: new Date().toISOString(),
                        status: 'deleted',
                        isDeleted: true,
                        metadata: {
                            original_metadata: {}
                        }
                    };
                }

                // For existing sources, handle each case
                switch (formData.sourceType) {
                    case 'files':
                        return {
                            id: source.id,
                            name: source.name || 'Unnamed File',
                            type: 'file',
                            source_type: source.source_type || 'file_upload',
                            created_at: source.created_at,
                            updated_at: source.updated_at,
                            status: source.status || 'active',
                            metadata: {
                                original_metadata: {
                                    mime_type: source.metadata?.mime_type || 'application/octet-stream'
                                }
                            }
                        };
                    case 'websites':
                        return {
                            id: source.id,
                            name: source.name || 'Unnamed Website',
                            type: 'web_source',
                            source_type: 'web_source',
                            created_at: source.created_at || new Date().toISOString(),
                            updated_at: source.updated_at,
                            status: source.status || 'active',
                            metadata: {
                                original_metadata: {
                                    url: source.configuration?.url,
                                    crawl_depth: source.configuration?.crawl_depth || 1,
                                    mime_type: 'text/html'
                                }
                            }
                        };
                    case 'apis':
                        const apiMethod = source.configuration?.method as ApiMethod || 'GET';
                        return {
                            id: source.id,
                            name: source.name || 'Unnamed API',
                            type: 'api_source',
                            source_type: 'api_source',
                            created_at: source.created_at,
                            updated_at: source.updated_at,
                            status: source.status || 'active',
                            metadata: {
                                original_metadata: {
                                    mime_type: 'application/json',
                                    url: (source.configuration?.endpoint || source.configuration?.url) || '',
                                    method: apiMethod
                                }
                            },
                            configuration: {
                                ...source.configuration,
                                method: apiMethod
                            }
                        };
                    default:
                        throw new Error('Invalid source type');
                }
            });

            const validationErrors = validateDataSources(processedSources);
            if (validationErrors.length > 0) {
                throw new Error(`Validation errors: ${validationErrors.join(', ')}`);
            }

            setSelectedDataSources(processedSources);

        } catch (err) {
            console.error('Error fetching data sources:', err);
            setFetchError(err instanceof Error ? err.message : 'Failed to fetch data sources');
            setSelectedDataSources([]);
        } finally {
            setIsLoadingDataSources(false);
            setDataSourceLoadingStates({});
        }
    }, [formData.dataSourceIds, formData.sourceType]);


    useEffect(() => {
        if (formData.sourceType && formData.dataSourceIds?.length) {
            fetchSelectedDataSources();
        }
    }, [formData.sourceType, formData.dataSourceIds.join(',')]);

    useEffect(() => {
        fetchSelectedDataSources();
    }, [fetchSelectedDataSources]);

    const validateDataSources = (sources: ExtendedDataSource[]): string[] => {
        const errors: string[] = [];

        sources.forEach(source => {
            if (!source.id) errors.push('Invalid source ID');
            // Remove name check since it can be null for deleted sources
            if (!source.isDeleted && !source.name) {
                errors.push(`Missing name for source ${source.id}`);
            }

            if (formData.sourceType === 'websites' && !source.isDeleted) {
                const metadata = source.metadata?.original_metadata as WebsiteMetadata;
                if (!metadata?.url) errors.push(`Missing URL for website ${source.name || source.id}`);
                if (typeof metadata?.crawl_depth !== 'number') {
                    errors.push(`Invalid crawl depth for website ${source.name || source.id}`);
                }
            }
        });

        return errors;
    };


    const renderDeleteButton = () => {
        if (mode !== 'edit' || !onDelete) return null;

        const isDisabled = isLoading || (kbUsage?.isInUse ?? false);

        return (
            <div
                className="position-relative d-inline-block"
                onMouseEnter={() => isDisabled && setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
            >
                <Button
                    variant="danger"
                    onClick={() => !isDisabled && setShowDeleteModal(true)}
                    disabled={isDisabled}
                    style={{ height: '38px' }}
                    className={`${isDisabled ? 'cursor-not-allowed' : ''}`}
                >
                    Delete
                </Button>
                {kbUsage?.isInUse && showTooltip && (
                    <div
                        className="position-absolute start-0 bg-dark text-white rounded shadow-sm"
                        style={{
                            bottom: 'calc(100% + 10px)',
                            fontSize: '0.875rem',
                            zIndex: 1000,
                            maxWidth: '100vw',
                            padding: '8px 12px',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <div className="mb-1">
                            Cannot delete - in use by: {kbUsage.applications.map(app => app.name).join(', ')}
                        </div>
                        <div
                            className="position-absolute"
                            style={{
                                bottom: '-6px',
                                left: '20px',
                                width: '12px',
                                height: '12px',
                                background: '#212529',
                                transform: 'rotate(45deg)'
                            }}
                        />
                    </div>
                )}
            </div>
        );
    };

    const handleSourceTypeChange = useCallback((value: string) => {
        if (mode === 'edit') return;
        onChange('sourceType', value);
        onChange('dataSourceIds', []);
        setSelectedDataSources([]);
        setFetchError(null);
    }, [mode, onChange]);

    const handleRemoveDataSource = (id: string) => {
        const newIds = formData.dataSourceIds.filter(dataId => dataId !== id);
        onChange('dataSourceIds', newIds);
    };

    const handleWebsiteSelect = (selectedWebsites: Array<{ id: string; crawlDepth: number }>) => {
        const websiteIds = selectedWebsites.map(website => website.id);
        onChange('dataSourceIds', websiteIds);
        fetchSelectedDataSources();
    };

    const handleApiSelect = (selectedApis: Array<{ id: string }>) => {
        const apiIds = selectedApis.map(api => api.id);
        onChange('dataSourceIds', apiIds);
        fetchSelectedDataSources();
    };

    const handleFileSelect = (selectedFiles: string[]) => {
        console.log('Handling file selection:', selectedFiles);
        onChange('dataSourceIds', selectedFiles);
        setShowFileModal(false);
        // Force a refresh of selected data sources
        setTimeout(() => {
            fetchSelectedDataSources();
        }, 0);
    };

    const handleModalError = useCallback((error: Error) => {
        setFetchError(error.message);
    }, []);

    const getWebsiteMetadata = (source: ExtendedDataSource) => {
        return source.metadata?.original_metadata as WebsiteMetadata;
    };

    const isSubmitDisabled =
        isLoading ||
        !formData.sourceType ||
        !formData.dataSourceIds?.length ||
        isLoadingDataSources ||
        Object.values(dataSourceLoadingStates).some(Boolean);

    const getMimeType = (source: ExtendedDataSource): string => {
        return source.metadata?.original_metadata?.mime_type?.toLowerCase() ||
            'text/plain';
    };

    return (
        <>
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-4">
                    {(error || fetchError) && (
                        <Alert variant="danger" className="mb-3 py-2 px-3">
                            {error || fetchError}
                        </Alert>
                    )}

                    {status === 'ready' && (
                        <Alert variant="info" className="mb-3">
                            This knowledge base is deployed. Only name and description can be modified.
                            To make other changes, please use the Clone action to create a new version.
                        </Alert>
                    )}

                    <Form onSubmit={onSubmit} className="mx-auto" style={{maxWidth: '1100px'}}>
                        <div className="mb-4">
                            {renderSectionHeader(
                                <FileIcon mimeType="text/plain" size={16} className="text-secondary"/>,
                                <>Knowledge Base Information <span className="text-danger">*</span></>
                            )}
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-medium small mb-1">
                                    <span className="text-danger me-1">*</span>
                                    Name
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => onChange('name', e.target.value)}
                                    required
                                    className="border"
                                    style={{ height: '38px' }}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-medium small mb-1">Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={formData.description}
                                    onChange={(e) => onChange('description', e.target.value)}
                                    className="border"
                                />
                            </Form.Group>
                        </div>

                        <div className="mb-4">
                            {renderSectionHeader(
                                <Database size={16} className="text-secondary"/>,
                                <>Data Source Type <span className="text-danger">*</span></>
                            )}
                            <div className="d-flex gap-3">
                                {sourceTypes.map((type) => (
                                    <label
                                        key={type.value}
                                        className={`
                                        position-relative py-2 px-4 rounded
                                        d-flex align-items-center gap-3
                                        border
                                        ${mode === 'edit' || status === 'ready'
                                            ? formData.sourceType === type.value
                                                ? 'border-primary bg-primary bg-opacity-10 cursor-not-allowed pointer-events-none'
                                                : 'border-light opacity-50 cursor-not-allowed pointer-events-none'
                                            : `hover:border-primary cursor-pointer ${
                                                formData.sourceType === type.value
                                                    ? 'border-primary bg-primary bg-opacity-10'
                                                    : 'border-light'
                                            }`
                                        }
                                        transition-all
                                    `}
                                        style={{
                                            transition: 'all 0.2s ease-in-out',
                                            minWidth: '140px',
                                            pointerEvents: mode === 'edit' || status === 'ready' ? 'none' : 'auto'
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            name="sourceType"
                                            value={type.value}
                                            checked={formData.sourceType === type.value}
                                            onChange={(e) => handleSourceTypeChange(e.target.value)}
                                            className="form-check-input mt-0"
                                            disabled={isFieldDisabled('sourceType')}  // Here
                                            style={{
                                                borderColor: '#bfbfd3',
                                                borderWidth: '2px'
                                            }}
                                        />
                                        <div className="d-flex align-items-center gap-2">
                                        <span
                                            className={`${formData.sourceType === type.value ? 'text-primary' : 'text-secondary'}`}>
                                            {type.icon}
                                        </span>
                                            <span className="fw-medium">{type.label}</span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {formData.sourceType && (
                            <div className="mb-4">
                                <Button
                                    variant="outline-primary"
                                    className="mb-3"
                                    onClick={() => {
                                        switch(formData.sourceType) {
                                            case 'files':
                                                setShowFileModal(true);
                                                break;
                                            case 'websites':
                                                setShowWebsiteModal(true);
                                                break;
                                            case 'apis':
                                                setShowApiModal(true);
                                                break;
                                        }
                                    }}
                                    disabled={isFieldDisabled('dataSourceIds')}  // Here
                                    style={{height: '38px'}}
                                >
                                    {formData.sourceType === 'files' && 'Select Files'}
                                    {formData.sourceType === 'websites' && (
                                        <>
                                            <Globe size={16} className="me-2"/>
                                            Select Websites
                                        </>
                                    )}
                                    {formData.sourceType === 'apis' && (
                                        <>
                                            <Code size={16} className="me-2"/>
                                            Select APIs
                                        </>
                                    )}
                                </Button>

                                {formData.dataSourceIds?.length > 0 && (
                                    <div className="border rounded">
                                        {isLoadingDataSources ? (
                                            <div className="p-3 text-center">
                                                <div className="spinner-border spinner-border-sm text-primary me-2"/>
                                                Loading data sources...
                                            </div>
                                        ) : (
                                            selectedDataSources.map(source => (
                                                <div key={source.id}>
                                                    {source.isDeleted ? (
                                                        <DeletedDataSourceItem
                                                            sourceType={formData.sourceType as "files" | "websites" | "apis"}
                                                            sourceId={source.id}
                                                            name={source.name}
                                                            originalMetadata={source.metadata?.original_metadata}
                                                        />
                                                    ) : (
                                                        <div className="p-2 border-bottom d-flex justify-content-between align-items-center">
                                                            <div className="d-flex align-items-center">
                                                                {formData.sourceType === 'files' && (
                                                                    <FileIcon
                                                                        mimeType={getMimeType(source)}
                                                                        size={20}
                                                                        className="me-2 text-secondary"
                                                                    />
                                                                )}
                                                                {formData.sourceType === 'websites' && (
                                                                    <Globe size={16} className="me-2 text-primary"/>
                                                                )}
                                                                {formData.sourceType === 'apis' && (
                                                                    <Code size={16} className="me-2 text-primary"/>
                                                                )}
                                                                <div>
                                                                    <span>{source.name}</span>
                                                                    {formData.sourceType === 'websites' && (
                                                                        <div className="text-muted small">
                                                                            {getWebsiteMetadata(source)?.url}
                                                                            {getWebsiteMetadata(source)?.crawl_depth !== undefined &&
                                                                                ` (Depth: ${getWebsiteMetadata(source)?.crawl_depth})`}
                                                                        </div>
                                                                    )}
                                                                    {formData.sourceType === 'apis' && (
                                                                        <div className="text-muted small">
                                                                            {source.metadata?.original_metadata?.url || source.configuration?.endpoint}
                                                                            {source.metadata?.original_metadata?.method &&
                                                                                ` (${source.metadata.original_metadata.method})`}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <Button
                                                                variant="link"
                                                                className="text-danger p-0"
                                                                onClick={() => handleRemoveDataSource(source.id)}
                                                                disabled={isFieldDisabled('dataSourceIds')}
                                                            >
                                                                <Trash2 size={16}/>
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}

                                {formData.sourceType !== 'apis' && (
                                    <div className="mb-4 mt-4">
                                        {renderSectionHeader(
                                            <Settings size={16} className="text-secondary"/>,
                                            <>Processing Settings <span className="text-danger">*</span></>
                                        )}
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <Form.Group>
                                                    <Form.Label className="fw-medium small mb-1">
                                                        <span className="text-danger me-1">*</span>
                                                        Embedding Model
                                                    </Form.Label>
                                                    <Form.Select
                                                        value={formData.embeddingModel}
                                                        onChange={(e) => onChange('embeddingModel', e.target.value)}
                                                        required
                                                        className="border"
                                                        disabled={isFieldDisabled('embeddingModel')}  // Here
                                                        style={{
                                                            height: '38px',
                                                            paddingTop: '0.375rem',
                                                            paddingBottom: '0.375rem'
                                                        }}
                                                    >
                                                        <option value="">Select embedding model</option>
                                                        {embeddingOptions.map(option => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </div>
                                            <div className="col-sm-6">
                                                <Form.Group>
                                                    <Form.Label className="fw-medium small mb-1 d-flex align-items-center">
                                                        <span className="text-danger me-1">*</span>
                                                        Chunk Size
                                                        <span className="ms-1 badge bg-secondary bg-opacity-10 text-secondary">
                                                        Default: 500
                                                    </span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        value={formData.chunkSize}
                                                        onChange={(e) => onChange('chunkSize', parseInt(e.target.value))}
                                                        min="100"
                                                        max="2000"
                                                        required
                                                        className="border"
                                                        disabled={isFieldDisabled('chunkSize')}
                                                        style={{height: '38px'}}
                                                    />
                                                </Form.Group>
                                            </div>
                                            <div className="col-sm-6">
                                                <Form.Group>
                                                    <Form.Label className="fw-medium small mb-1 d-flex align-items-center">
                                                        <span className="text-danger me-1">*</span>
                                                        Overlap Size
                                                        <span className="ms-1 badge bg-secondary bg-opacity-10 text-secondary">
                                                        Default: 50
                                                    </span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        value={formData.overlap}
                                                        onChange={(e) => onChange('overlap', parseInt(e.target.value))}
                                                        min="0"
                                                        max="200"
                                                        required
                                                        className="border"
                                                        disabled={isFieldDisabled('overlap')}  // Here
                                                        style={{height: '38px'}}
                                                    />
                                                </Form.Group>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Form Actions */}
                        <div className="d-flex justify-content-between align-items-center pt-3 mt-3">
                            {renderDeleteButton()}

                            <div className={`d-flex gap-2 ${mode === 'edit' ? '' : 'ms-auto'}`}>
                                <Button
                                    variant="light"
                                    onClick={() => window.history.back()}
                                    disabled={isLoading}
                                    style={{ height: '38px' }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={isSubmitDisabled}
                                    style={{ height: '38px' }}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status"/>
                                            {mode === 'edit' ? 'Saving...' : 'Creating...'}
                                        </>
                                    ) : (
                                        mode === 'edit' ? 'Save' : 'Create Knowledge Base'
                                    )}
                                </Button>
                            </div>
                        </div>
                    </Form>
                </Card.Body>

                {/* Modals */}
                {formData.sourceType === 'files' && (
                    <FileSelectionModal
                        show={showFileModal}
                        onHide={() => setShowFileModal(false)}
                        onSelect={handleFileSelect}
                        onError={handleModalError}
                        currentSelected={formData.dataSourceIds}
                        key={formData.dataSourceIds.join(',')}
                    />
                )}

                {formData.sourceType === 'websites' && (
                    <WebsiteSelectionModal
                        show={showWebsiteModal}
                        onHide={() => setShowWebsiteModal(false)}
                        onSelect={handleWebsiteSelect}
                        currentSelected={selectedDataSources
                            .filter(source => getWebsiteMetadata(source)?.crawl_depth !== undefined)
                            .map(source => ({
                                id: source.id,
                                crawlDepth: getWebsiteMetadata(source)?.crawl_depth || 2
                            }))}
                    />
                )}

                {formData.sourceType === 'apis' && (
                    <ApiSelectionModal
                        show={showApiModal}
                        onHide={() => setShowApiModal(false)}
                        onSelect={handleApiSelect}
                        currentSelected={selectedDataSources.map(source => ({
                            id: source.id
                        }))}
                    />
                )}
            </Card>
            {mode === 'edit' && onDelete && (
                <DeleteConfirmationModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={() => onDelete?.()}
                    resourceType="Knowledge Base"
                    resourceName={formData.name}
                    isLoading={isLoading}
                />
            )}
        </>
    );
};

export default KnowledgeBaseForm;